/**
 * Operator sign-in handoff (PKCE style). The OIDC login finishes in the
 * system browser, which sets no cookie. The Mini App webview proves it started
 * the login by presenting a secret whose SHA-256 challenge it sent up front,
 * and the backend then sets the Operator Session cookie in the webview.
 *
 * The secret lives only in the closures below: never in a URL, storage or log.
 */
import { isHttpsUrl } from "../../shared/host/adapter";

export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

type HandoffOutcome =
  "authenticated" | "expired" | "forbidden" | "timeout" | "aborted";

const HANDOFF_INTERVAL_MS = 2_000;
const HANDOFF_DEADLINE_MS = 5 * 60 * 1_000;

const LOGIN_URL = "/operator/auth/login";
const HANDOFF_URL = "/operator/auth/handoff";
const SECRET_BYTES = 32;
const TERMINAL_STATUS: Readonly<Record<number, HandoffOutcome | undefined>> = {
  200: "authenticated",
  404: "expired",
  403: "forbidden",
};

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** 32 random bytes as 43 base64url characters, without padding. */
export function createHandoffSecret(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(SECRET_BYTES)));
}

/** base64url(SHA-256(ASCII bytes of the secret)), without padding. */
export async function handoffChallenge(secret: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(secret),
  );
  return base64Url(new Uint8Array(digest));
}

interface PollHandoffOptions {
  secret: string;
  fetchImpl?: FetchLike;
  intervalMs?: number;
  deadlineMs?: number;
  signal?: AbortSignal;
  /** Source of visibilitychange events; defaults to the document. */
  visibilityTarget?: Document;
}

/**
 * Polls the handoff endpoint until it resolves. One attempt is in flight at a
 * time; an attempt also fires immediately when the page becomes visible again.
 */
export function pollHandoff({
  secret,
  fetchImpl = (input, init) => fetch(input, init),
  intervalMs = HANDOFF_INTERVAL_MS,
  deadlineMs = HANDOFF_DEADLINE_MS,
  signal,
  visibilityTarget = document,
}: PollHandoffOptions): Promise<HandoffOutcome> {
  return new Promise<HandoffOutcome>((resolve) => {
    let done = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attemptAbort: AbortController | undefined;

    const finish = (outcome: HandoffOutcome) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      clearTimeout(deadlineTimer);
      visibilityTarget.removeEventListener("visibilitychange", onVisible);
      signal?.removeEventListener("abort", onAbort);
      attemptAbort?.abort();
      resolve(outcome);
    };
    const onAbort = () => {
      finish("aborted");
    };
    const schedule = () => {
      if (!done) timer = setTimeout(() => void attempt(), intervalMs);
    };
    const attempt = async () => {
      if (done || inFlight) return;
      clearTimeout(timer);
      inFlight = true;
      attemptAbort = new AbortController();
      try {
        const response = await fetchImpl(HANDOFF_URL, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ secret }),
          signal: attemptAbort.signal,
        });
        // 202 (pending) and anything unexpected: keep trying until the deadline.
        const outcome = TERMINAL_STATUS[response.status];
        if (outcome !== undefined) {
          inFlight = false;
          finish(outcome);
          return;
        }
      } catch {
        // Network error: transient.
      }
      inFlight = false;
      schedule();
    };
    const onVisible = () => {
      if (visibilityTarget.visibilityState === "visible") void attempt();
    };
    const deadlineTimer = setTimeout(() => {
      finish("timeout");
    }, deadlineMs);

    if (signal?.aborted) {
      finish("aborted");
      return;
    }
    signal?.addEventListener("abort", onAbort);
    visibilityTarget.addEventListener("visibilitychange", onVisible);
    schedule();
  });
}

interface OperatorLoginOptions extends Omit<PollHandoffOptions, "secret"> {
  openExternal: (url: string) => void;
  /** Message of the error thrown when the login URL cannot be obtained. */
  failureMessage: string;
}

/**
 * Starts the handoff: asks for the login URL bound to the challenge, opens it
 * in the system browser, then polls until the webview holds the session.
 * Throws when the login URL cannot be obtained.
 */
export async function runOperatorLogin({
  openExternal,
  failureMessage,
  fetchImpl = (input, init) => fetch(input, init),
  signal,
  ...pollOptions
}: OperatorLoginOptions): Promise<HandoffOutcome> {
  const secret = createHandoffSecret();
  const challenge = await handoffChallenge(secret);
  const response = await fetchImpl(
    `${LOGIN_URL}?handoff_challenge=${encodeURIComponent(challenge)}`,
    { credentials: "include", signal },
  );
  if (!response.ok) throw new Error(failureMessage);
  const body: unknown = await response.json();
  if (
    typeof body !== "object" ||
    body === null ||
    !("authorization_url" in body) ||
    typeof body.authorization_url !== "string" ||
    !isHttpsUrl(body.authorization_url)
  )
    throw new Error(failureMessage);
  if (signal?.aborted) return "aborted";
  openExternal(body.authorization_url);
  return pollHandoff({ ...pollOptions, secret, fetchImpl, signal });
}
